import { useMemo } from 'react';

import {
  AVAILABLE_CANDIDATES_GROUP_BY_LOCALSTORAGE_KEY,
  OrderDirection,
} from '@constants';
import {
  AvailableFromFilter,
  AvailableProfileOption,
  canonicalSeniority,
  CandidateRegion,
  IAvailableCandidateFilters,
} from '@utils';
import { IAvailableCandidate } from '@types';
import {
  AvailableCandidateColumnId,
  isAvailableCandidateColumnId,
} from 'components/availableCandidates/columns';
import {
  IListViewBase,
  IListViewConfig,
  splitParam,
  useListViewState,
} from './useListViewState';

type NoGrouping = 'none';

interface IAvailableCandidatesFilterState extends IAvailableCandidateFilters {
  showSkipped: boolean;
}

export interface IAvailableCandidatesViewState
  extends IListViewBase<NoGrouping, AvailableCandidateColumnId>,
    IAvailableCandidatesFilterState {}

export const EmptyAvailableCandidateFilters: IAvailableCandidateFilters = {
  categories: [],
  skills: [],
  primarySkills: [],
  seniorities: [],
  regions: [],
  availableFrom: 'any',
  profile: [],
  ttMin: null,
};

const Categories: IAvailableCandidate['category'][] = [
  'agileengineer',
  'prescreened',
];

const ProfileOptions: AvailableProfileOption[] = ['fullTime', 'ranked'];

const AvailableFromOptions: AvailableFromFilter[] = [
  'any',
  'immediately',
  'within30',
];

const DefaultSort = { key: '', direction: OrderDirection.ASC };

export const useAvailableCandidatesViewState = (
  defaultColumns: AvailableCandidateColumnId[],
) => {
  const config = useMemo(
    (): IListViewConfig<
      IAvailableCandidatesFilterState,
      NoGrouping,
      AvailableCandidateColumnId
    > => ({
      emptyFilters: { ...EmptyAvailableCandidateFilters, showSkipped: false },
      readFilters: (params) => {
        const from = params.get('from') as AvailableFromFilter;
        const tt = Number(params.get('tt'));

        return {
          categories: splitParam(params.get('category')).filter(
            (value): value is IAvailableCandidate['category'] =>
              Categories.includes(value as IAvailableCandidate['category']),
          ),
          skills: splitParam(params.get('skills')),
          primarySkills: splitParam(params.get('primary')),
          seniorities: splitParam(params.get('seniority')).map(
            canonicalSeniority,
          ),
          regions: splitParam(params.get('region')).filter(
            (value): value is CandidateRegion =>
              Object.values(CandidateRegion).includes(value as CandidateRegion),
          ),
          availableFrom: AvailableFromOptions.includes(from) ? from : 'any',
          profile: splitParam(params.get('profile')).filter(
            (value): value is AvailableProfileOption =>
              ProfileOptions.includes(value as AvailableProfileOption),
          ),
          ttMin: params.get('tt') && Number.isFinite(tt) ? tt : null,
          showSkipped: params.get('skipped') === 'true',
        };
      },
      writeFilters: (filters, set) => {
        set('category', filters.categories.join(','));
        set('skills', filters.skills.join(','));
        set('primary', filters.primarySkills.join(','));
        set('seniority', filters.seniorities.join(','));
        set('region', filters.regions.join(','));
        set(
          'from',
          filters.availableFrom === 'any' ? null : filters.availableFrom,
        );
        set('profile', filters.profile.join(','));
        set('tt', filters.ttMin === null ? null : String(filters.ttMin));
        set('skipped', filters.showSkipped ? 'true' : null);
      },
      countFilters: (filters) =>
        filters.categories.length +
        filters.skills.length +
        filters.primarySkills.length +
        filters.seniorities.length +
        filters.regions.length +
        filters.profile.length +
        (filters.availableFrom === 'any' ? 0 : 1) +
        (filters.ttMin === null ? 0 : 1),
      groupOptions: ['none'],
      defaultGroup: 'none',
      groupStorageKey: AVAILABLE_CANDIDATES_GROUP_BY_LOCALSTORAGE_KEY,
      defaultColumns,
      isColumn: isAvailableCandidateColumnId,
      defaultSort: DefaultSort,
    }),
    [defaultColumns],
  );

  return useListViewState(config);
};
